import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { WidgetPeriods } from '../types'

import { getT12WPeriod } from '@/pages/dashboard/lib/helpers'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { getISOWeek, getISOWeekYear } from 'date-fns'
import { prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts'

import { DateAggregation, useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { ampli } from '@/shared/lib/ampli'
import { PALETTE } from '@/shared/lib/theme-palette'
import { cn } from '@/shared/lib/utils'
import { intlFormat } from '@/shared/tools/dates'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { MainDashboardFilter } from '../../Filter'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

export default function HistoricalPriceAcceptanceWidget({
	className,
	commonFilters,
	commonPeriods,
}: {
	className?: string
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const filters = [...commonFilters, ...(params.filters ?? [])]
	const currency = useSelectedCurrency()
	const commonQueryParams = {
		currency,
		label: 'HistoricalPriceAcceptance',
		...(commonPeriods?.main || {
			start_date: null,
			end_date: null,
		}),
		skip_dates: true,
		metrics: [
			'accepted',
			'not_accepted',
			'locked',
			'unassigned',
			'changed_manually',
			'accepted_percent',
			'not_accepted_percent',
			'locked_percent',
			'unassigned_percent',
			'changed_manually_percent',
		],
		dimensions: [],
		filters,
	}
	const biQuery = useBIAnalyticQuery(
		{
			...commonQueryParams,
			...getT12WPeriod(commonPeriods.main.end_date),
			date_aggregation: DateAggregation.WeekKey,
		},
		{
			select: (data) => zipSeries(data?.meta, data?.data),
		}
	)
	const chartConfig = {
		accepted: {
			label: t('accepted'),
			color: PALETTE.green[75],
		},
		changed_manually: {
			label: t('changed_manually'),
			color: PALETTE.red[75],
		},
		unassigned: {
			label: t('unassigned'),
			color: PALETTE.orange[75],
		},
		locked: {
			label: t('locked'),
			color: PALETTE.blue[75],
		},
		not_accepted: {
			label: t('not_accepted'),
			color: PALETTE.neutral[150],
		},
	} satisfies ChartConfig

	return (
		<Widget className={cn('relative flex flex-auto flex-col', className)}>
			<WidgetHeader
				className='pb-0'
				title={t('widget.price_acceptance_dashboard.title')}
				subtitle={t('widget.historical_price_acceptance.subtitle', {
					defaultValue: 'Weekly CY, 12-week period',
				})}
				tooltip={t('widget.historical_price_acceptance.tooltip')}
			>
				<div className='flex items-center gap-2'>
					<MainDashboardFilter
						irremovableCount={commonFilters.length}
						filters={filters ?? []}
						onChange={(filters) => {
							ampli.dashboardBiApplyFilterHistoricalPriceAcceptance({
								'filter by': filters.map(prop('name')).join(','),
							})
							onChangeParams({ ...params, filters })
						}}
					/>
				</div>
			</WidgetHeader>
			<WidgetContent className='mt-0 pb-0'>
				<WidgetLoadingState isTinted={biQuery.isLoading} />
				<WidgetErrorState isTinted={biQuery.isError} />
				<ChartContainer config={chartConfig} className='h-62 w-full'>
					<BarChart accessibilityLayer={false} data={biQuery.data || []}>
						<CartesianGrid vertical={false} strokeDasharray='3 3' />
						<XAxis
							dataKey='week_key'
							axisLine={false}
							fontSize={10}
							interval={0}
							tickFormatter={(value) => {
								const date = new Date(value)
								const shortYear = String(getISOWeekYear(date))?.slice(-2)

								return `${t('general.week_short', { defaultValue: 'W' })}${getISOWeek(date)}'${shortYear}`
							}}
						/>
						<ChartTooltip
							content={
								<ChartTooltipContent
									labelFormatter={(value) =>
										typeof value === 'string' || typeof value === 'number'
											? intlFormat(new Date(value))
											: ''
									}
									formatter={(value, name, item, index, payload) => (
										<div className='flex w-full items-center gap-2'>
											<div
												className='h-2.5 w-2.5 shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)'
												style={
													{
														'--color-bg': item.fill,
														'--color-border': item.fill,
													} as React.CSSProperties
												}
											/>
											<span className='text-background/50'>
												{t(name as string, { defaultValue: name as string })}
											</span>
											<div className='ml-auto flex items-center gap-0.5'>
												<span className='font-mono font-semibold text-background tabular-nums'>
													{value?.toLocaleString()}
												</span>
												<span className='font-mono font-medium text-background tabular-nums'>
													(
													{formatNumber(
														prop(`${name}_percent`, payload),
														NumberFormats.Percent
													)}
													)
												</span>
											</div>
										</div>
									)}
								/>
							}
						/>
						<ChartLegend content={<ChartLegendContent />} />
						<Bar
							maxBarSize={50}
							dataKey='not_accepted'
							stackId='a'
							fill='var(--color-not_accepted)'
							stroke='var(--color-background)'
							strokeWidth={1}
							radius={[0, 0, 4, 4]}
						/>
						<Bar
							maxBarSize={50}
							dataKey='locked'
							stackId='a'
							fill='var(--color-locked)'
							stroke='var(--color-background)'
							strokeWidth={1}
						/>
						<Bar
							maxBarSize={50}
							dataKey='unassigned'
							stackId='a'
							fill='var(--color-unassigned)'
							stroke='var(--color-background)'
							strokeWidth={1}
						/>
						<Bar
							maxBarSize={50}
							dataKey='changed_manually'
							stackId='a'
							fill='var(--color-changed_manually)'
							stroke='var(--color-background)'
							strokeWidth={1}
						/>
						<Bar
							maxBarSize={50}
							dataKey='accepted'
							stackId='a'
							fill='var(--color-accepted)'
							stroke='var(--color-background)'
							strokeWidth={1}
							radius={[4, 4, 0, 0]}
						/>
					</BarChart>
				</ChartContainer>
			</WidgetContent>
		</Widget>
	)
}
